import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, AbstractControl, Validators } from "@angular/forms";

import { Observable } from "rxjs";
import { switchMap } from "rxjs/operators";

//import { GridOptions, ColDef } from "@ag-grid-enterprise/all-modules";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { ApplicationsService } from "../../../../applications/services/applications.service";
import { AuthService } from "../../../../services/auth.service";
import { SidePanelService } from "../../../../common/services/side-panel.service";
import { ApplicationNotesService } from "../../../../applications/services/application-notes.service";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { WellService } from "../../../../common/services/well.service";
import { IUploadFile, DocumentManagementService } from '../../../../common/services/document-management.service';
import { GisQueryService } from "../../../../gis/services/gis-query.service";

import { SubHeader } from "../../../../core/models/sub-header.model";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { WorkflowData, BulkNAD83Data, ApplicationStateChangeRequest, ApplicationState } from "../../../../applications/models/applications.models";
import { ParserError } from "../../../../builder/components/state/controls/config/data/file-parser-config/parser.model";


import { ApplicationBaseComponent } from "../../../../applications/components/application-base.component";

@Component({
    selector: 'app-approve-nad83-updates',
    templateUrl: './approve-nad83-updates.component.html',
    standalone: false
})
export class ApproveNad83UpdatesComponent extends ApplicationBaseComponent implements OnInit, OnDestroy {

	approvedDialog: Dialog = new Dialog("Application #[APPNUMBER] has been approved.", [new DialogButton("OK", true, () => { this.home(); })]);
	confirmApproveDialog: Dialog = new Dialog("", [new DialogButton("NO", false, () => { return; }), new DialogButton("Yes", true, () => { this.approve(); })]);
	confirmApproveDialogText: string = "Application #[APPNUMBER] will be approved.  Only the NAD83 Latitude and Longitude data in rows without errors will be updated; rows with errors will need to be uploaded via a new application.  This process is irreversible; do you wish to continue?";
	confirmRejectDialog: Dialog = new Dialog("", [new DialogButton("NO", false, () => { return; }), new DialogButton("Yes", true, () => { this.reject(); })]);
	rejectDialogText: string = "Application #[APPNUMBER] will be rejected.  This process is irreversible; you will have to start a new application if you change your mind.  Do you wish to continue?";
	rejectDialog: Dialog = new Dialog("Application #[APPNUMBER] has been rejected.", [new DialogButton("OK", true, () => { this.home(); })]);


	private _data: BulkNAD83Data[];

	get data(): BulkNAD83Data[] {
		if (this._data != null) return this._data;
		if (this.form == null) return [];
		let ctrl = this.form.get('parsedData');
		if (ctrl == null || ctrl.value == null) return [];
		this._data = JSON.parse(ctrl.value);
		return this._data;
	}

	set data(value: BulkNAD83Data[]) {
		if (this.form == null) return;
		let ctrl = this.form.get('parsedData');
		if (ctrl == null) return;
		this._data = null;
		ctrl.setValue(JSON.stringify(value));
	}

	get dataColumns(): ColDef[] {
		return [
			{
				headerClass: "wrapped",
				headerName: "API #",
				headerTooltip: "API #",
				field: "apiNumber",
				tooltipField: "apiNumber",
				suppressMovable: true,
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams,
				maxWidth: 125
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Latitude (NAD83)",
				headerTooltip: "Latitude (NAD83)",
				field: "latitude83",
				tooltipField: "latitude83",
				suppressMovable: true,
				filter: "agNumberColumnFilter",
				filterParams: this._formatter.defaultNumberFilterParams,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Longitude (NAD83)",
				headerTooltip: "Longitude (NAD83)",
				field: "longitude83",
				tooltipField: "longitude83",
				suppressMovable: true,
				filter: "agNumberColumnFilter",
				filterParams: this._formatter.defaultNumberFilterParams,
				maxWidth: 150
			} as ColDef,
			{
				headerClass: "wrapped",
				headerName: "Source of Location",
				headerTooltip: "Source of Location",
				field: "sourceOfLocation",
				tooltipField: "sourceOfLocation",
				suppressMovable: true,
				filter: "agTextColumnFilter",
				filterParams: this._formatter.defaultTextFilterParams
			}

		] as ColDef[];
	}

	@ViewChild("downloadLink") public downloadLink: ElementRef;

	get errorGridColumns(): ColDef[] {
		return [
			{
				headerName: "Line",
				headerTooltip: "Line",
				field: "line",
				tooltipField: "line",
				suppressMovable: true,
				maxWidth: 100
			} as ColDef,
			{
				headerName: "Column",
				headerTooltip: "Column",
				field: "column",
				tooltipField: "column",
				suppressMovable: true,
				minWidth: 125,
				maxWidth: 125
			} as ColDef,
			{
				headerName: "Error",
				headerTooltip: "Error",
				field: "error",
				tooltipField: "error",
				suppressMovable: true
			} as ColDef
		];
	}

	errorGridOptions: GridOptions;
	errorGridApi?: GridApi;
	private _errors: ParserError[];

	get errors(): ParserError[] {
		if (this._errors != null) return this._errors;
		if (this.form == null) return [];
		let ctrl = this.form.get('errors');
		if (ctrl == null || ctrl.value == null) return [];
		this._errors = JSON.parse(ctrl.value);
		return this._errors;
	}

	set errors(value: ParserError[]) {
		if (this.form == null) return;
		let ctrl = this.form.get('errors');
		if (ctrl == null) return;
		this._errors = null;
		ctrl.setValue(JSON.stringify(value));
	}

	get fileCtrl(): AbstractControl {
		if (this.form == null) return null;
		return <AbstractControl>this.form.get("files");
	}

	get files(): IUploadFile[] {
		if (this.fileCtrl == null) return [];
		return <IUploadFile[]>this.fileCtrl.value;
	}

	get hasFileListing(): boolean {
		return this.files != null && this.files.length > 0;
	}

	endStates: ApplicationState[] = [ApplicationState.Approved, ApplicationState.Withdrawn];
	inputStates: ApplicationState[] = [ApplicationState.Input, ApplicationState.Start, ApplicationState.Corrections];


	parsedGridOptions: GridOptions;
	parsedGridApi?: GridApi;
	statusBar = {
		statusPanels: [
			{
				statusPanel: 'agFilteredRowCountComponent',
				align: 'right',
			},
			{
				statusPanel: 'agTotalRowCountComponent',
				align: 'right',
			}
		]
	};

	private subheader: SubHeader = new SubHeader({ title: "Approve NAD83 Updates - Application #[APPNUMBER]", buttons: [{ name: "Cancel", method: () => this.home() },  { name: "Approve", method: () => this.confirmSubmit() }, { name: "Reject", method: () => this.confirmReject() }] });


	constructor(private route: ActivatedRoute, private _builder: FormBuilder, _router: Router, _subheaderService: SubHeaderService, _dialog: DynamicDialogService, _auth: AuthService, private _docService: DocumentManagementService,
		_loader: LoaderService, _applications: ApplicationsService, _notes: ApplicationNotesService, _sidePanel: SidePanelService, private _wells: WellService, private _formatter: GridFormatterService, private _queryService: GisQueryService) {
		super(_loader, _dialog, _subheaderService, _auth, _applications, _router, _sidePanel, _notes);
		this.fileFields = ['files'];
	}

	ngOnInit() {
		this.subheaderService.updateSubHeader(this.subheader);
		super.ngOnInit();
		if (this.route.snapshot.data.application) {
			this.application = this.route.snapshot.data.application;
			if (this.inputStates.includes(this.application.applicationState)) { //application is not in review state; redirect to the input page
				let url = `/admin/bulk-nad83-update/${this.application.applicationNumber}`;
				this.router.navigate([url]);
			} else if (this.endStates.includes(this.application.applicationState)) {
				let url = `/admin/bulk-nad83-update/complete/${this.application.applicationNumber}`;
				this.router.navigate([url]);
			}
			this.subheader.title = this.subheader.title.replace("[APPNUMBER]", this.application.applicationNumber);
			this.subheaderService.updateSubHeader(this.subheader);
		}
		this.buildForm(this.application);
		this.setupGrids();
	}

	approve(): void {
		let obs$: Observable<WorkflowData> = this.saveProgress()
			.pipe(
				switchMap((app: WorkflowData) => {
					let request: ApplicationStateChangeRequest = {
						applicationId: app.id,
						currentState: app.applicationState,
						requestedState: ApplicationState.Approved
					} as ApplicationStateChangeRequest;
					return this.dataService.routeApplication(request);
				})
		);
		this._loader.toggleLoader(true);
		let subs$ = obs$.pipe()
			.subscribe({
				next: (data: WorkflowData) => {
					this._loader.toggleLoader(false);
					this.approvedDialog.message = this.approvedDialog.message.replace("[APPNUMBER]", data.applicationNumber);
					this.approvedDialog.toggleDialog(true);
					this.dialogService.updateDialog(this.approvedDialog);
				},
				error: (error) => {
					this.handleError(error);
					this._loader.toggleLoader(false);
				}
		});
		this.subscription.add(subs$);
	}

	buildForm(data: WorkflowData | null = null): void {
		this.form = this._builder.group({
			files: [[], [Validators.required, Validators.minLength(1)]],
			parsedData: [null, [Validators.required]],
			errors: [null, []]
		});
		if (data != null) {
			this.form = this.restoreFormData(this.form);
		}
		this.formReady$.next(true);
	}

	confirmReject(): void {
		this.confirmRejectDialog.message = this.rejectDialogText.replace('[APPNUMBER]', this.application.applicationNumber);
		this.confirmRejectDialog.toggleDialog(true);
		this.dialogService.updateDialog(this.confirmRejectDialog);
	}

	confirmSubmit(): void {
		this.confirmApproveDialog.message = this.confirmApproveDialogText.replace('[APPNUMBER]', this.application.applicationNumber);
		this.confirmApproveDialog.toggleDialog(true);
		this.dialogService.updateDialog(this.confirmApproveDialog);
	}

	reject(): void {
		let obs$: Observable<WorkflowData> = this.saveProgress().pipe(
			switchMap((app: WorkflowData) => {
				let request: ApplicationStateChangeRequest = {
					applicationId: app.id,
					currentState: app.applicationState,
					requestedState: ApplicationState.Withdrawn
				} as ApplicationStateChangeRequest;
				return this.dataService.routeApplication(request);
			})
		);
		this._loader.toggleLoader(true);
		let subs$ = obs$.pipe()
			.subscribe(
				(app: WorkflowData) => {
					this.rejectDialog.message = this.rejectDialog.message.replace('[APPNUMBER]', app.applicationNumber);
					this._loader.toggleLoader(false);
					this.rejectDialog.toggleDialog(true);
					this.dialogService.updateDialog(this.rejectDialog);
				},
				(error) => {
					console.error(error);
					this.dialogService.showError();
					this._loader.toggleLoader(false);
				}
			);
		this.subscription.add(subs$);
	}

	setupGrids(): void {
		this.errorGridOptions = this._formatter.BuildDefaultGridOptionsWithColumns(this.errorGridColumns, false, true, true, true);
		this.parsedGridOptions = this._formatter.BuildDefaultGridOptionsWithColumns(this.dataColumns, false, true, true, true);
		this.parsedGridOptions.headerHeight = 50;
		this.parsedGridOptions.pagination = false;
		this.parsedGridOptions.onGridReady = (params) => {
			this.parsedGridApi = params.api;
		}
		this.parsedGridOptions.onFirstDataRendered = () => {
			if (this.parsedGridApi != null) {
				this.parsedGridApi.sizeColumnsToFit();
			}
		}
		this.errorGridOptions.pagination = false;
		this.errorGridOptions.onGridReady = (params) => {
			this.errorGridApi = params.api;
			this.errorGridApi.sizeColumnsToFit();
		};
		this.errorGridOptions.onFirstDataRendered = () => { 
			if (this.errorGridApi != null) {
				this.errorGridApi.sizeColumnsToFit();
			}
		 }
	}
}
